import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Gauge,
  Info,
  RefreshCw,
  RotateCcw,
  Target,
  TrendingUp,
  Users2
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import backendApi from '../../services/backendApi';
import { useAuth } from '../../context/AuthContext';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { computeBurndown } from '../../utils/dashboardMetrics';
import SprintBurndownChart from './SprintBurndownChart';

const STATUS_STYLES = {
  good: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  danger: 'bg-rose-50 text-rose-700 border-rose-200',
  neutral: 'bg-slate-50 text-slate-500 border-slate-200'
};

const formatNumber = value => value == null ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(value);
const formatDays = value => value == null ? '—' : `${formatNumber(value)} j`;
const formatPct = value => value == null ? '—' : `${formatNumber(value)} %`;

const TableMetricHeader = ({ children, explanation }) => (
  <th className="px-4 py-4">
    <span className="cursor-help border-b border-dashed border-slate-300" title={explanation}>{children}</span>
  </th>
);

const ProjectFilter = ({ projects, selectedId, onChange }) => (
  <div className="relative min-w-[230px]">
    <select
      aria-label="Filtrer par projet"
      value={selectedId || ''}
      onChange={event => onChange(event.target.value ? Number(event.target.value) : null)}
      className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-200"
    >
      <option value="">Tous les projets</option>
      {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
    </select>
    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
  </div>
);

const MetricCard = ({ label, value, note, formula, fields, icon: Icon, tone = 'purple' }) => {
  const tones = {
    purple: 'bg-purple-50 text-purple-700',
    indigo: 'bg-indigo-50 text-indigo-700',
    amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700',
    rose: 'bg-rose-50 text-rose-700'
  };
  return (
    <article className="group relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-md">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">{label}</p>
          <p className="mt-3 font-mono text-3xl font-semibold tracking-tight text-slate-900">{value}</p>
        </div>
        <button
          type="button"
          aria-label={`Explication du KPI ${label}`}
          className={`rounded-xl p-2.5 ${tones[tone]} focus:outline-none focus:ring-2 focus:ring-purple-300`}
        >
          <Icon className="h-5 w-5" />
        </button>
      </div>
      <p className="mt-3 text-xs leading-5 text-slate-500">{note}</p>
      <div role="tooltip" className="pointer-events-none invisible absolute left-4 right-4 top-[4.5rem] z-30 rounded-2xl border border-slate-200 bg-slate-950 p-4 text-left text-white opacity-0 shadow-2xl transition duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-purple-200"><Info className="h-3.5 w-3.5" /> Comment c’est calculé</p>
        <p className="mt-2 text-xs font-semibold leading-5 text-white">{formula}</p>
        <p className="mt-2 border-t border-white/10 pt-2 font-mono text-[10px] leading-4 text-slate-400">Champs : {fields}</p>
      </div>
    </article>
  );
};

const TeamPerformancePage = () => {
  const { isInternalUser } = useAuth();
  const navigate = useNavigate();
  const [scope, setScope] = useState('sprint');
  const [projectId, setProjectId] = useState(null);
  const [profile, setProfile] = useState('po');
  const [data, setData] = useState(null);
  const [burndown, setBurndown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isInternalUser) navigate('/dashboard', { replace: true });
  }, [isInternalUser, navigate]);

  const fetchData = useCallback(() => {
    if (!isInternalUser) return;
    setLoading(true);
    setError(null);
    const requests = [backendApi.getTeamPerformance(scope, projectId)];
    if (scope === 'sprint') requests.push(backendApi.getSprintBurndown());
    Promise.all(requests)
      .then(([performance, sprintBurndown]) => {
        setData(performance);
        setBurndown(sprintBurndown || null);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [isInternalUser, scope, projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useAutoRefresh(fetchData, 5 * 60 * 1000);

  const filteredBurndown = useMemo(() => {
    if (!burndown || scope !== 'sprint') return null;
    if (!projectId) return burndown;
    const tasks = (burndown.tasks || []).filter(task => task.projectId === projectId);
    return computeBurndown(tasks, burndown.sprintStart, burndown.sprintEnd);
  }, [burndown, projectId, scope]);

  if (!isInternalUser) return null;

  if (loading && !data) {
    return <div className="flex min-h-[65vh] items-center justify-center"><RefreshCw className="h-8 w-8 animate-spin text-purple-600" /></div>;
  }

  if (error && !data) {
    return (
      <div className="px-8 pt-8">
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
          <AlertTriangle className="h-8 w-8 text-rose-600" />
          <p className="text-sm font-medium text-rose-700">{error}</p>
          <button onClick={fetchData} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700">Réessayer</button>
        </div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const alerts = data?.alerts || {};
  const rows = profile === 'po' ? data?.productOwners || [] : data?.developers || [];
  const bottleneck = (data?.flow || []).filter(step => step.days != null).sort((a, b) => b.days - a.days)[0];

  return (
    <div className="min-h-full bg-[#F4F7FA] px-5 pb-10 pt-6 sm:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-purple-700">
            <Users2 className="h-4 w-4" /> Pilotage de la livraison
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-950">Performance équipe</h1>
          <p className="mt-1 text-sm text-slate-500">
            {summary.totalUS || 0} US analysées · synchronisé {data?.generatedAt ? new Date(data.generatedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
            {[
              ['sprint', 'Sprint actif'],
              ['30d', '30 jours'],
              ['90d', '90 jours'],
              ['all', 'Tout']
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => setScope(value)}
                className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${scope === value ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-900'}`}
              >{label}</button>
            ))}
          </div>
          <ProjectFilter projects={data?.projects || []} selectedId={projectId} onChange={setProjectId} />
          <button onClick={fetchData} disabled={loading} aria-label="Actualiser" className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-600 shadow-sm hover:text-purple-700 disabled:opacity-50">
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </header>

      {error && <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">Dernière actualisation impossible : {error}</div>}

      <section className="mb-6 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Flux de livraison médian</p>
            <h2 className="mt-1 text-lg font-bold text-slate-900">Où le travail ralentit réellement</h2>
          </div>
          {bottleneck && <div className="rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">Goulot : {bottleneck.label} · {formatDays(bottleneck.days)}</div>}
        </div>
        <div className="overflow-x-auto px-6 py-7">
          <div className="flex min-w-[960px] items-center">
            {(data?.flow || []).map((step, index) => (
              <React.Fragment key={step.key}>
                <div className={`min-w-[108px] rounded-2xl border px-4 py-4 text-center ${STATUS_STYLES[step.status]}`}>
                  <p className="text-xs font-bold">{step.label}</p>
                  <p className="mt-2 font-mono text-xl font-semibold">{formatDays(step.days)}</p>
                  <p className="mt-1 text-[10px] opacity-70">{step.owner} · {step.sampleSize} US</p>
                </div>
                {index < data.flow.length - 1 && <div className="flex flex-1 items-center px-2"><div className="h-px flex-1 bg-slate-200" /><ArrowRight className="h-4 w-4 text-slate-300" /></div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <MetricCard label="Engagement" value={formatPct(summary.commitmentPct)} note={`${formatNumber(summary.completedSP)} SP traités sur ${formatNumber(summary.totalSP)}`} formula="SP traités ÷ SP engagés × 100." fields="x_studio_story_points · étape/date de test" icon={Target} tone="purple" />
        <MetricCard label="Vélocité" value={`${formatNumber(summary.velocitySP)} SP`} note="Story Points arrivés en test ou Done" formula="Somme des Story Points des US traitées pendant le périmètre sélectionné." fields="x_studio_story_points · x_studio_date_test_1" icon={TrendingUp} tone="indigo" />
        <MetricCard label="Refaisage" value={formatPct(summary.reworkPct)} note="SP de refaisage rapportés à la charge" formula="SP de refaisage ÷ SP engagés × 100." fields="x_studio_story_points_refaisage · x_studio_story_points" icon={RotateCcw} tone="rose" />
        <MetricCard label="Validation PO" value={formatDays(summary.validationDays)} note="Médiane entre mise à disposition pour validation et Done" formula="Médiane de (date Done − date mise à disposition pour validation), pour les US validées." fields="x_studio_date_test_client · x_studio_date_done_1" icon={Clock3} tone="amber" />
        <MetricCard label="Livré à l’heure" value={formatPct(summary.onTimePct)} note="Livraison réelle comparée à la date prévue" formula="Livraisons dans les délais ÷ livraisons ayant une date de référence × 100." fields="x_studio_date_de_livraison · date_deadline" icon={CheckCircle2} tone="emerald" />
      </section>

      <section className="mb-6 grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(300px,1fr)]">
        <div className="min-h-[390px] rounded-3xl border border-slate-200 bg-white p-1 shadow-sm">
          {scope === 'sprint' && filteredBurndown ? (
            <SprintBurndownChart sprintData={filteredBurndown} />
          ) : (
            <div className="p-6">
              <div className="mb-6">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Vélocité</p>
                <h2 className="mt-1 text-lg font-bold text-slate-900">SP mis en test par jour</h2>
              </div>
              <div className="h-[285px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data?.velocitySeries || []} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                    <defs><linearGradient id="velocityFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#714B67" stopOpacity={0.28} /><stop offset="100%" stopColor="#714B67" stopOpacity={0.02} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} tickFormatter={value => new Date(`${value}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
                    <Tooltip formatter={value => [`${value} SP`, 'Traités']} labelFormatter={value => new Date(`${value}T00:00:00`).toLocaleDateString('fr-FR')} />
                    <Area type="monotone" dataKey="sp" stroke="#714B67" strokeWidth={3} fill="url(#velocityFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        <aside className="rounded-3xl border border-slate-200 bg-slate-950 p-6 text-white shadow-sm">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">À traiter</p>
              <h2 className="mt-1 text-lg font-bold">Alertes prioritaires</h2>
            </div>
            <Gauge className="h-6 w-6 text-purple-300" />
          </div>
          <div className="space-y-3">
            {[
              { label: 'US sans estimation', value: alerts.unestimated, tone: 'text-amber-300' },
              { label: 'US immobiles depuis plus de 5 jours', value: alerts.aging, tone: 'text-rose-300' },
              { label: 'Validations PO en attente', value: alerts.pendingValidation, tone: 'text-orange-300' },
              { label: 'Ajouts après le début du sprint', value: alerts.lateSprintAdditions, tone: 'text-purple-300' },
              { label: 'Story Points de refaisage', value: alerts.reworkSP, tone: 'text-sky-300' }
            ].map(alert => (
              <div key={alert.label} className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-4">
                <span className="text-sm leading-5 text-slate-300">{alert.label}</span>
                <span className={`font-mono text-2xl font-semibold ${alert.tone}`}>{formatNumber(alert.value)}</span>
              </div>
            ))}
          </div>
        </aside>
      </section>

      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 px-6 py-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Lecture par profil</p>
            <h2 className="mt-1 text-lg font-bold text-slate-900">Indicateurs individuels de coaching</h2>
          </div>
          <div className="flex rounded-xl bg-slate-100 p-1">
            <button onClick={() => setProfile('po')} className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${profile === 'po' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500'}`}>Product Owners</button>
            <button onClick={() => setProfile('dev')} className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${profile === 'dev' ? 'bg-white text-purple-700 shadow-sm' : 'text-slate-500'}`}>Développeurs</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[780px] text-left">
            <thead className="bg-slate-50 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
              <tr>
                <th className="px-6 py-4">Collaborateur</th>
                <th className="px-4 py-4">Charge</th>
                {profile === 'po' ? <>
                  <TableMetricHeader explanation="Médiane de la date mise à disposition pour validation moins la date Test. Champs : x_studio_date_test_1 et x_studio_date_test_client.">Test → mise à disposition pour validation</TableMetricHeader>
                  <TableMetricHeader explanation="Médiane de la date d’entrée en sprint moins la date de création de l’US. Champs : create_date et x_studio_date_sprint.">Création → sprint</TableMetricHeader>
                  <TableMetricHeader explanation="Médiane de la date de création de l’US moins le début de la réunion liée. Champs : x_studio_runion et calendar.event.start.">Réunion → création</TableMetricHeader>
                </> : <>
                  <th className="px-4 py-4">SP traités</th><th className="px-4 py-4">Engagement</th><th className="px-4 py-4">Cycle dev</th><th className="px-4 py-4">Refaisage</th>
                </>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(row => (
                <tr key={row.id} className="transition hover:bg-slate-50/80">
                  <td className="px-6 py-4"><div className="font-semibold text-slate-900">{row.name}</div><div className="mt-0.5 text-xs text-slate-400">{row.totalUS} US</div></td>
                  <td className="px-4 py-4 font-mono text-sm text-slate-700">{profile === 'po' ? `${row.activeUS} actives` : `${formatNumber(row.assignedSP)} SP`}</td>
                  {profile === 'po' ? <>
                    <td className="px-4 py-4 font-mono text-sm font-semibold text-slate-800">{formatDays(row.testToClientDays)}</td>
                    <td className="px-4 py-4 font-mono text-sm text-slate-700">{formatDays(row.creationToSprintDays)}</td>
                    <td className="px-4 py-4 font-mono text-sm text-slate-700">{formatDays(row.meetingToCreationDays)}</td>
                  </> : <>
                    <td className="px-4 py-4 font-mono text-sm font-semibold text-slate-800">{formatNumber(row.completedSP)}</td>
                    <td className="px-4 py-4 font-mono text-sm text-slate-700">{formatPct(row.commitmentPct)}</td>
                    <td className="px-4 py-4 font-mono text-sm text-slate-700">{formatDays(row.cycleDays)}</td>
                    <td className="px-4 py-4 font-mono text-sm text-slate-700">{formatPct(row.reworkPct)}</td>
                  </>}
                </tr>
              ))}
              {!rows.length && <tr><td colSpan="6" className="px-6 py-12 text-center text-sm text-slate-400">Aucune donnée disponible pour ce périmètre.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default TeamPerformancePage;
